import React from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Chip, Divider } from '@material-ui/core';
import { Card, CardHeader, CardContent } from '@material-ui/core';
import { GetApp } from '@material-ui/icons';

import { Actions as Mepbay } from '../../../store/ducks/mepbay';
import { ImageChooserForm } from '../map';
import { exportImagesAsCsv } from '../visualization/imageCsv';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * MEPBay image chooser card.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Mepbay
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginBottom: 0,
    marginLeft: 12,
  },
  item: {
    margin: 0,
    padding: 0,
  },
  chip: {
    marginLeft: theme.spacing(1),
  },
}));

/**
 * @function ImageChooserCardMepBay
 * @description Card that lists the available MEPBay image dates and lets the user request an
 * individual image's acquisition.
 * @param {Object} props - Component properties.
 * @param {string} [props.className] - Extra CSS class (currently unused by this component).
 * @see {@link ImageChooserForm}
 * **Called by:
 * @see {SidePanel}
 * @memberof module:Mepbay
 * @returns {JSX.Element|null} The image chooser card, or null until availableDates has loaded.
 */
const ImageChooserCardMepBay = ({ className }) => {
  const availableDates = useSelector((state) => state.mepbay.availableDates);
  const satellite = useSelector((state) => state.mepbay.satellite, shallowEqual);
  const classes = useStyles();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  return availableDates === undefined ? null : (
    <CardContent>
      <Button
        variant="outlined"
        startIcon={<GetApp />}
        disabled={!availableDates.length}
        style={{ marginBottom: 8 }}
        onClick={() => exportImagesAsCsv(availableDates, 'mepbay-selected-dates.csv')}>
        {t('forms.imageChooser.exportDates')}
      </Button>
      <div id="imageChooserForm">
        <ImageChooserForm
          images={availableDates}
          disabledPredicate={(i) => false}
          onLoadRequested={(i) => dispatch(Mepbay.acquireImage(availableDates[i].name, availableDates[i].date))}
          formatter={(i) => satellite.get(availableDates[i].name).format}
        />
      </div>
    </CardContent>
  );
};

export default ImageChooserCardMepBay;
